import { useEffect, useState } from "react";
import { Bitcoin, Landmark } from "lucide-react";
import api from "../lib/api";
import { usd, clsx } from "../lib/format";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export const feeFor = (m, amt) => (m ? Math.round(((m.fee_fixed || 0) + (amt * (m.fee_percent || 0)) / 100) * 100) / 100 : 0);
export const limitText = (m, floor = 0) => {
  const lo = Math.max(m?.min_amount || 0, floor);
  return [lo ? `Min ${usd(lo)}` : "", m?.max_amount ? `Max ${usd(m.max_amount)}` : ""].filter(Boolean).join(" · ");
};
const feeText = (m) => [m.fee_fixed ? usd(m.fee_fixed) : "", m.fee_percent ? `${m.fee_percent}%` : ""].filter(Boolean).join(" + ") || "No fee";

function Field({ label, k, wd, setWd, ph, mono }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={wd.details[k] || ""} onChange={(e) => setWd({ ...wd, details: { ...wd.details, [k]: e.target.value } })} placeholder={ph} data-testid={`withdraw-detail-${k}`} className={clsx("bg-fx-base border-white/10", mono && "font-mono")} />
    </div>
  );
}

export default function WithdrawForm({ wd, setWd, liveBal, onMethods }) {
  const [methods, setMethods] = useState(null);
  useEffect(() => {
    api.get("/payments/withdrawal-methods").then((r) => {
      setMethods(r.data); onMethods?.(r.data);
      if (r.data.length && !r.data.some((m) => m.id === wd.method_id)) setWd((w) => ({ ...w, method_id: r.data[0].id, details: {} }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (methods === null) return <p className="text-sm text-fx-s3">Loading…</p>;
  if (!methods.length) return <p className="text-sm text-fx-s3 py-6 text-center" data-testid="withdraw-no-methods">No withdrawal methods are available right now. Please contact support.</p>;
  const m = methods.find((x) => x.id === wd.method_id) || methods[0];
  const amt = Number(wd.amount_usd) || 0;
  const fee = feeFor(m, amt);

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-fx-base border border-white/5 p-3 flex justify-between"><span className="text-sm text-fx-s3">Available</span><span className="font-mono">{usd(liveBal)}</span></div>
      <div className="space-y-1.5">
        <Label>Method</Label>
        <div className="grid grid-cols-2 gap-2">
          {methods.map((x) => {
            const I = x.kind === "crypto" ? Bitcoin : Landmark;
            return (
              <button key={x.id} onClick={() => setWd({ ...wd, method_id: x.id, details: {} })} data-testid={`withdraw-method-${x.id}`}
                className={clsx("text-left rounded-lg border p-2.5 transition-colors", x.id === m.id ? "bg-brandblue/15 border-brandblue" : "bg-fx-base border-white/10 hover:border-white/25")}>
                <div className="flex items-center gap-1.5 text-sm font-semibold"><I className="h-3.5 w-3.5 text-brandblue" /> <span className="truncate">{x.label}</span></div>
                <div className="text-[10px] text-fx-s3 mt-0.5">Fee: {feeText(x)}</div>
              </button>
            );
          })}
        </div>
      </div>
      {m.instructions && <p className="text-xs text-fx-s3 whitespace-pre-line">{m.instructions}</p>}
      <div className="space-y-1">
        <Label>Amount (USD)</Label>
        <Input data-testid="withdraw-amount-input" type="number" value={wd.amount_usd} onChange={(e) => setWd({ ...wd, amount_usd: e.target.value })} className="bg-fx-base border-white/10 font-mono" />
        <p className="text-[11px] text-fx-s3" data-testid="withdraw-limits">{limitText(m, 10)}</p>
      </div>
      {m.kind === "crypto" ? (
        <Field label={`Your ${m.coin} wallet address (${m.network} network)`} k="address" wd={wd} setWd={setWd} mono ph="Paste your receiving address" />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Bank name" k="bank_name" wd={wd} setWd={setWd} />
          <Field label="Account name" k="account_name" wd={wd} setWd={setWd} />
          <Field label="Account number" k="account_number" wd={wd} setWd={setWd} mono />
          <Field label="Routing / Sort code" k="routing_code" wd={wd} setWd={setWd} mono ph="Optional" />
        </div>
      )}
      <div className="rounded-lg bg-fx-base border border-white/5 px-3 py-2 text-sm space-y-1" data-testid="withdraw-fee-summary">
        <div className="flex justify-between"><span className="text-fx-s3">Amount</span><span className="font-mono">{usd(amt)}</span></div>
        <div className="flex justify-between"><span className="text-fx-s3">Fee ({feeText(m)})</span><span className="font-mono text-neg" data-testid="withdraw-fee">-{usd(fee)}</span></div>
        <div className="flex justify-between border-t border-white/5 pt-1 font-semibold"><span>You receive</span><span className="font-mono text-pos" data-testid="withdraw-net">{usd(Math.max(0, amt - fee))}</span></div>
      </div>
    </div>
  );
}
